<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import GwIcon from '@/components/GwIcon.vue'
import { computed, onMounted, nextTick } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'

const { get, locale, webRoute, alternateLocales } = useWebTranslations()
const { refresh } = useScrollReveal()

const seoTitle = computed(() => get('pages.backoffice.seo_title'))
const seoDescription = computed(() => get('pages.backoffice.seo_description'))
const canonicalPath = computed(() => webRoute('web.backoffice'))
const pageAlternateLocales = computed(() => alternateLocales('web.backoffice'))

const featuresHref = computed(() => `${webRoute('web.features')}#backoffice`)

const trustItems = ['all_in_one', 'easy', 'insights']

const navItems = [
  'dashboard', 'contacts', 'companies', 'offers', 'orders', 'invoices',
  'expenses', 'opportunities', 'calendar', 'documents', 'reports', 'settings',
]

const kpis = [
  { key: 'revenue', value: '€24,860', trend: '+18.2%', tone: '#3B82F6' },
  { key: 'invoices', value: '€6,540', trend: '+4.1%', tone: '#F59754' },
  { key: 'offers', value: '18', trend: '+2', tone: '#A855F7' },
  { key: 'opportunities', value: '12', trend: '+3', tone: '#22A06B' },
]

const callouts = [
  {
    key: 'contacts',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.12)',
    icon: 'users',
  },
  {
    key: 'offers',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.12)',
    icon: 'document',
  },
  {
    key: 'finance',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.14)',
    icon: 'chart-bar',
  },
  {
    key: 'opportunities',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.12)',
    icon: 'sparkle',
  },
  {
    key: 'documents',
    accent: '#7C3AED',
    accentSoft: 'rgba(124,58,237,0.12)',
    icon: 'folder-open',
  },
]

const featureCards = [
  {
    key: 'faster',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.14)',
    icon: 'bolt',
  },
  {
    key: 'time',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.14)',
    icon: 'clock',
  },
  {
    key: 'visibility',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.16)',
    icon: 'eye',
  },
  {
    key: 'decisions',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.14)',
    icon: 'chart-bar',
  },
  {
    key: 'grow',
    accent: '#7C3AED',
    accentSoft: 'rgba(124,58,237,0.14)',
    icon: 'network',
  },
]

const activities = [1, 2, 3, 4]
const customers = [
  { tone: '#C4B5FD', amount: '€8,420' },
  { tone: '#F9A8D4', amount: '€6,180' },
  { tone: '#93C5FD', amount: '€4,950' },
]
const tasks = [1, 2, 3]

const documentFeatures = [
  {
    key: 'folders',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.12)',
    icon: 'folder-open',
  },
  {
    key: 'search',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.12)',
    icon: 'search',
  },
  {
    key: 'links',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.14)',
    icon: 'users',
  },
  {
    key: 'anywhere',
    accent: '#3B82F6',
    accentSoft: 'rgba(59,130,246,0.12)',
    icon: 'cloud',
  },
  {
    key: 'secure',
    accent: '#7C3AED',
    accentSoft: 'rgba(124,58,237,0.12)',
    icon: 'shield',
  },
  {
    key: 'admin',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.12)',
    icon: 'bolt',
  },
]

const docFolders = [
  { key: 'contracts', tone: '#A855F7', soft: 'rgba(168,85,247,0.14)', count: 24 },
  { key: 'offers', tone: '#22A06B', soft: 'rgba(34,160,107,0.14)', count: 18 },
  { key: 'invoices', tone: '#F59754', soft: 'rgba(245,151,84,0.16)', count: 42 },
  { key: 'delivery', tone: '#3B82F6', soft: 'rgba(59,130,246,0.14)', count: 11 },
  { key: 'internal', tone: '#FF43A0', soft: 'rgba(255,67,160,0.14)', count: 10 },
]

const docRows = [
  { type: 'PDF', typeTone: '#EF4444', name: 'Pogodba_ABC_d.o.o.pdf', linked: 'ABC d.o.o.', tag: 'contract', tagTone: '#A855F7', date: '20. 5. 2024', size: '245 KB' },
  { type: 'DOC', typeTone: '#3B82F6', name: 'Ponudba_XYZ_2024.docx', linked: 'XYZ d.o.o.', tag: 'offer', tagTone: '#22A06B', date: '18. 5. 2024', size: '128 KB' },
  { type: 'XLS', typeTone: '#22A06B', name: 'Pregled_stroskov_Q1.xlsx', linked: 'Interno', tag: 'internal', tagTone: '#F59754', date: '15. 5. 2024', size: '512 KB' },
  { type: 'PDF', typeTone: '#EF4444', name: 'Racuni_april_2024.pdf', linked: 'ABC d.o.o.', tag: 'invoice', tagTone: '#FF43A0', date: '12. 5. 2024', size: '890 KB' },
  { type: 'PDF', typeTone: '#EF4444', name: 'Dobavnica_7841.pdf', linked: 'Partner d.o.o.', tag: 'delivery', tagTone: '#3B82F6', date: '10. 5. 2024', size: '96 KB' },
]

onMounted(() => {
  nextTick(() => refresh())
})
</script>

<template>
  <SEOHead
    :title="seoTitle"
    :description="seoDescription"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <section class="relative overflow-hidden bg-[#F7F4FB]">
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <div
        class="absolute -top-24 -right-20 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-40"
        style="background: radial-gradient(circle, rgba(241,0,254,0.18) 0%, transparent 70%);"
      ></div>
      <div
        class="absolute -bottom-28 -left-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-35"
        style="background: radial-gradient(circle, rgba(245,151,84,0.22) 0%, transparent 70%);"
      ></div>
    </div>

    <div class="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-10 pb-14 lg:pt-14 lg:pb-20">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-start">

        <!-- Copy -->
        <div class="lg:col-span-4 reveal lg:pt-2">
          <span
            class="inline-flex items-center rounded-full px-3.5 py-1.5 text-[0.7rem] font-semibold tracking-[0.14em] uppercase text-white"
            style="background: linear-gradient(135deg, #A855F7 0%, #FF43A0 100%);"
          >
            {{ get('pages.backoffice.badge') }}
          </span>

          <h1 class="mt-5 text-[2.05rem] sm:text-[2.5rem] lg:text-[2.85rem] font-bold tracking-[-0.035em] leading-[1.1] text-[color:var(--gw-ink)] text-balance">
            {{ get('pages.backoffice.heading_line1') }}
            <span
              class="block mt-1"
              style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
            >{{ get('pages.backoffice.heading_line2') }}</span>
            <span
              class="block"
              style="background: linear-gradient(135deg, #FF43A0 0%, #F100FE 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
            >{{ get('pages.backoffice.heading_line3') }}</span>
          </h1>

          <p class="mt-5 text-[1.02rem] leading-[1.65] text-[rgba(51,51,51,0.68)] max-w-md text-pretty">
            {{ get('pages.backoffice.lead') }}
          </p>

          <div class="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a href="https://admin.growiyo.com" class="gw-btn-grad touch-manipulation justify-center">
              {{ get('home.hero.cta_start') }}
            </a>
            <a
              :href="webRoute('web.demo')"
              :aria-label="get('home.hero.cta_watch_aria')"
              class="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full bg-white text-[color:var(--gw-ink)] text-sm font-semibold border border-[rgba(51,51,51,0.12)] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(241,0,254,0.35)] touch-manipulation"
            >
              <span
                class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%);"
                aria-hidden="true"
              >
                <GwIcon name="play" class="w-3 h-3 text-white ml-0.5" />
              </span>
              {{ get('home.hero.cta_watch') }}
            </a>
          </div>

          <p class="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.875rem] text-[rgba(51,51,51,0.62)]">
            <template v-for="(item, i) in trustItems" :key="item">
              <span class="inline-flex items-center gap-1.5">
                <GwIcon name="check" class="w-3.5 h-3.5 text-[#22A06B] shrink-0" />
                {{ get(`pages.backoffice.trust.${item}`) }}
              </span>
              <span v-if="i < trustItems.length - 1" class="text-[rgba(51,51,51,0.28)]" aria-hidden="true">•</span>
            </template>
          </p>
        </div>

        <!-- Dashboard + callouts -->
        <div class="lg:col-span-8 relative reveal reveal-delay-1">
          <div class="grid grid-cols-1 xl:grid-cols-[1fr_9.5rem] gap-3 items-start">

            <!-- Dashboard mock -->
            <div
              class="rounded-2xl overflow-hidden bg-white shadow-[0_32px_70px_-20px_rgba(51,51,51,0.40)] border border-[rgba(51,51,51,0.08)]"
              aria-hidden="true"
            >
              <div class="flex items-center gap-1.5 px-3 py-2 border-b border-[rgba(51,51,51,0.06)] bg-[#FAFAFA]">
                <span class="w-2 h-2 rounded-full bg-[#FF5F57]"></span>
                <span class="w-2 h-2 rounded-full bg-[#FEBC2E]"></span>
                <span class="w-2 h-2 rounded-full bg-[#28C840]"></span>
                <span class="ml-2 flex-1 h-5 rounded-md bg-[rgba(51,51,51,0.05)] text-[0.6rem] text-[rgba(51,51,51,0.4)] flex items-center px-2">
                  app.growiyo.com/backoffice
                </span>
              </div>

              <div class="flex min-h-[320px] sm:min-h-[380px]">
                <!-- Sidebar -->
                <div class="hidden sm:flex w-[7.25rem] shrink-0 flex-col bg-[#1E1B2E] py-2.5 px-1.5 gap-0.5">
                  <div
                    v-for="(item, i) in navItems"
                    :key="item"
                    class="flex items-center gap-1.5 rounded-lg px-1.5 py-1.5"
                    :class="i === 0 ? 'bg-white/10' : ''"
                  >
                    <span
                      class="w-4 h-4 rounded flex items-center justify-center shrink-0"
                      :class="i === 0 ? 'text-[#FF43A0]' : 'text-white/45'"
                    >
                      <GwIcon name="menu" class="w-2.5 h-2.5" />
                    </span>
                    <span
                      class="text-[0.5rem] leading-none truncate"
                      :class="i === 0 ? 'text-white font-semibold' : 'text-white/45'"
                    >
                      {{ get(`pages.backoffice.mock.nav.${item}`) }}
                    </span>
                  </div>
                </div>

                <!-- Main -->
                <div class="flex-1 min-w-0 bg-[#F8F7FC] p-2.5 sm:p-3">
                  <!-- Top bar -->
                  <div class="flex items-center gap-2 mb-2.5">
                    <div class="flex-1 h-7 rounded-lg bg-white border border-[rgba(51,51,51,0.08)] flex items-center px-2.5 gap-1.5">
                      <GwIcon name="search" class="w-3 h-3 text-[rgba(51,51,51,0.35)]" />
                      <span class="text-[0.6rem] text-[rgba(51,51,51,0.35)]">{{ get('pages.backoffice.mock.search') }}</span>
                    </div>
                    <span class="w-7 h-7 rounded-lg bg-white border border-[rgba(51,51,51,0.08)] flex items-center justify-center text-[color:var(--gw-ink)] text-sm font-semibold">+</span>
                    <span class="relative w-7 h-7 rounded-lg bg-white border border-[rgba(51,51,51,0.08)] flex items-center justify-center">
                      <GwIcon name="bell" class="w-3.5 h-3.5 text-[rgba(51,51,51,0.45)]" />
                      <span class="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#EF4444]"></span>
                    </span>
                    <span class="w-7 h-7 rounded-full" style="background: linear-gradient(135deg, #F59754, #F100FE);"></span>
                  </div>

                  <!-- KPI row -->
                  <div class="grid grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-2 mb-2">
                    <div
                      v-for="kpi in kpis"
                      :key="kpi.key"
                      class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5"
                    >
                      <p class="text-[0.5rem] sm:text-[0.55rem] uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] font-semibold truncate">
                        {{ get(`pages.backoffice.mock.kpis.${kpi.key}`) }}
                      </p>
                      <div class="mt-0.5 flex items-end justify-between gap-1">
                        <p class="text-[0.85rem] sm:text-[0.95rem] font-bold tracking-[-0.02em] text-[color:var(--gw-ink)]">{{ kpi.value }}</p>
                        <span class="text-[0.5rem] font-semibold text-[#22A06B]">{{ kpi.trend }}</span>
                      </div>
                      <svg class="mt-1 w-full h-5" viewBox="0 0 80 20" preserveAspectRatio="none">
                        <path d="M0 14 C15 12, 25 6, 40 9 C55 12, 65 4, 80 6" fill="none" :stroke="kpi.tone" stroke-width="1.8"/>
                      </svg>
                    </div>
                  </div>

                  <!-- Middle widgets -->
                  <div class="grid grid-cols-5 gap-1.5 sm:gap-2 mb-2">
                    <div class="col-span-2 rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5">
                      <p class="text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] mb-1.5">
                        {{ get('pages.backoffice.mock.activity') }}
                      </p>
                      <div v-for="n in activities" :key="n" class="flex items-center gap-1.5 mb-1.5 last:mb-0">
                        <span class="w-4 h-4 rounded-md shrink-0" :style="{ background: ['#A855F7','#FF43A0','#22A06B','#F59754'][n-1], opacity: 0.2 }"></span>
                        <span class="h-1.5 flex-1 rounded-full bg-[rgba(51,51,51,0.08)]"></span>
                        <span class="h-1 w-5 rounded-full bg-[rgba(51,51,51,0.05)]"></span>
                      </div>
                    </div>
                    <div class="col-span-3 rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5">
                      <p class="text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] mb-1">
                        {{ get('pages.backoffice.mock.sales') }}
                      </p>
                      <svg class="w-full h-16 sm:h-20" viewBox="0 0 200 60" preserveAspectRatio="none">
                        <path d="M0 48 C25 44, 40 30, 60 34 C80 38, 95 18, 120 22 C145 26, 165 10, 200 14" fill="none" stroke="#A855F7" stroke-width="2.2"/>
                        <path d="M0 48 C25 44, 40 30, 60 34 C80 38, 95 18, 120 22 C145 26, 165 10, 200 14 L200 60 L0 60 Z" fill="rgba(168,85,247,0.10)"/>
                      </svg>
                    </div>
                  </div>

                  <!-- Bottom widgets -->
                  <div class="grid grid-cols-3 gap-1.5 sm:gap-2">
                    <div class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5">
                      <p class="text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] mb-1.5">
                        {{ get('pages.backoffice.mock.customers') }}
                      </p>
                      <div v-for="(c, i) in customers" :key="i" class="flex items-center gap-1.5 mb-1.5 last:mb-0">
                        <span class="w-4 h-4 rounded-full shrink-0" :style="{ background: c.tone }"></span>
                        <span class="h-1.5 flex-1 rounded-full bg-[rgba(51,51,51,0.08)]"></span>
                        <span class="text-[0.5rem] font-semibold text-[color:var(--gw-ink)]">{{ c.amount }}</span>
                      </div>
                    </div>
                    <div class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5 flex flex-col items-center">
                      <p class="self-start text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] mb-1">
                        {{ get('pages.backoffice.mock.offers_status') }}
                      </p>
                      <svg class="w-14 h-14 sm:w-16 sm:h-16" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#EDE9FE" stroke-width="5"/>
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#A855F7" stroke-width="5" stroke-dasharray="35 88" stroke-dashoffset="0" transform="rotate(-90 18 18)"/>
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#FF43A0" stroke-width="5" stroke-dasharray="25 88" stroke-dashoffset="-35" transform="rotate(-90 18 18)"/>
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#22A06B" stroke-width="5" stroke-dasharray="18 88" stroke-dashoffset="-60" transform="rotate(-90 18 18)"/>
                        <circle cx="18" cy="18" r="14" fill="none" stroke="#F59754" stroke-width="5" stroke-dasharray="10 88" stroke-dashoffset="-78" transform="rotate(-90 18 18)"/>
                      </svg>
                    </div>
                    <div class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 sm:p-2.5">
                      <p class="text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[rgba(51,51,51,0.45)] mb-1.5">
                        {{ get('pages.backoffice.mock.tasks') }}
                      </p>
                      <div v-for="n in tasks" :key="n" class="flex items-center gap-1.5 mb-1.5 last:mb-0">
                        <span class="w-3 h-3 rounded border border-[rgba(51,51,51,0.2)] shrink-0" :class="n === 1 ? 'bg-[#22A06B] border-[#22A06B]' : ''"></span>
                        <span class="h-1.5 flex-1 rounded-full bg-[rgba(51,51,51,0.08)]"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Side callouts -->
            <ul class="hidden xl:flex flex-col gap-2.5 list-none p-0 m-0 pt-6">
              <li
                v-for="item in callouts"
                :key="item.key"
                class="relative rounded-xl bg-white border border-[rgba(51,51,51,0.08)] shadow-[0_10px_28px_-16px_rgba(51,51,51,0.35)] px-2.5 py-2.5"
              >
                <span
                  class="w-7 h-7 rounded-lg flex items-center justify-center mb-1.5"
                  :style="{ background: item.accentSoft, color: item.accent }"
                  aria-hidden="true"
                >
                  <GwIcon :name="item.icon" class="w-3.5 h-3.5" />
                </span>
                <p class="text-[0.7rem] font-bold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-snug">
                  {{ get(`pages.backoffice.callouts.${item.key}`) }}
                </p>
              </li>
            </ul>
          </div>

          <!-- Mobile/tablet callouts -->
          <ul class="xl:hidden mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 list-none p-0 m-0">
            <li
              v-for="item in callouts"
              :key="`m-${item.key}`"
              class="rounded-xl bg-white border border-[rgba(51,51,51,0.08)] px-3 py-2.5 flex items-center gap-2.5"
            >
              <span
                class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                :style="{ background: item.accentSoft, color: item.accent }"
              >
                <GwIcon :name="item.icon" class="w-4 h-4" />
              </span>
              <p class="text-[0.75rem] font-semibold text-[color:var(--gw-ink)] leading-snug">
                {{ get(`pages.backoffice.callouts.${item.key}`) }}
              </p>
            </li>
          </ul>
        </div>
      </div>

      <!-- Promo bar -->
      <div class="mt-8 lg:mt-10 reveal flex justify-center">
        <div class="inline-flex items-center gap-3 rounded-full bg-white border border-[rgba(168,85,247,0.12)] shadow-[0_12px_40px_-16px_rgba(168,85,247,0.35)] px-4 sm:px-5 py-3 max-w-full">
          <span
            class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style="background: rgba(168,85,247,0.14); color: #A855F7;"
            aria-hidden="true"
          >
            <GwIcon name="bolt" class="w-4 h-4" />
          </span>
          <div class="min-w-0 text-left">
            <p class="text-[0.875rem] sm:text-[0.95rem] font-semibold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-snug">
              {{ get('pages.backoffice.promo.before') }}
              <span class="text-[#A855F7]">{{ get('pages.backoffice.promo.highlight') }}</span>
              {{ get('pages.backoffice.promo.after') }}
            </p>
            <p class="mt-0.5 text-[0.75rem] sm:text-[0.8rem] text-[rgba(51,51,51,0.55)] leading-snug">
              {{ get('pages.backoffice.promo.subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Feature cards -->
      <div class="mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 reveal">
        <article
          v-for="card in featureCards"
          :key="card.key"
          class="rounded-[1.25rem] bg-white border border-[rgba(51,51,51,0.06)] p-5 shadow-[0_10px_28px_-18px_rgba(51,51,51,0.28)] transition-transform duration-300 hover:-translate-y-1"
        >
          <span
            class="w-10 h-10 rounded-[0.75rem] flex items-center justify-center"
            :style="{ background: card.accentSoft, color: card.accent }"
            aria-hidden="true"
          >
            <GwIcon :name="card.icon" class="w-5 h-5" />
          </span>
          <h2 class="mt-4 text-[0.95rem] font-bold tracking-[-0.015em] text-[color:var(--gw-ink)]">
            {{ get(`pages.backoffice.features.${card.key}.title`) }}
          </h2>
          <p class="mt-1.5 text-[0.8rem] leading-[1.55] text-[rgba(51,51,51,0.62)]">
            {{ get(`pages.backoffice.features.${card.key}.body`) }}
          </p>
        </article>
      </div>

      <!-- Document system -->
      <div id="dokumentni-sistem" class="mt-16 lg:mt-20 reveal">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">

          <!-- Left copy -->
          <div class="lg:col-span-5">
            <div class="flex items-center gap-2.5">
              <span
                class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style="background: rgba(168,85,247,0.12); color: #A855F7;"
                aria-hidden="true"
              >
                <GwIcon name="document" class="w-4 h-4" />
              </span>
              <p class="text-[0.75rem] font-semibold uppercase tracking-[0.16em] text-[#A855F7]">
                {{ get('pages.backoffice.documents.label') }}
              </p>
            </div>

            <h2 class="mt-4 text-[1.85rem] sm:text-[2.25rem] font-bold tracking-[-0.03em] leading-[1.15] text-[color:var(--gw-ink)] text-balance">
              {{ get('pages.backoffice.documents.heading_line1') }}
              <span class="block">{{ get('pages.backoffice.documents.heading_line2') }}</span>
              <span
                class="block"
                style="background: linear-gradient(135deg, #A855F7 0%, #FF43A0 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
              >{{ get('pages.backoffice.documents.heading_emphasis') }}</span>
            </h2>

            <p class="mt-4 text-[1rem] leading-[1.65] text-[rgba(51,51,51,0.68)] max-w-md text-pretty">
              {{ get('pages.backoffice.documents.lead') }}
            </p>

            <div class="mt-5 w-12 h-0.5 rounded-full bg-[#A855F7]" aria-hidden="true"></div>

            <ul class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 list-none p-0 m-0">
              <li
                v-for="item in documentFeatures"
                :key="item.key"
                class="flex items-start gap-3"
              >
                <span
                  class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  :style="{ background: item.accentSoft, color: item.accent }"
                  aria-hidden="true"
                >
                  <GwIcon :name="item.icon" class="w-4 h-4" />
                </span>
                <div class="min-w-0">
                  <p class="text-[0.875rem] font-bold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-snug">
                    {{ get(`pages.backoffice.documents.features.${item.key}.title`) }}
                  </p>
                  <p class="mt-0.5 text-[0.775rem] leading-[1.5] text-[rgba(51,51,51,0.58)]">
                    {{ get(`pages.backoffice.documents.features.${item.key}.body`) }}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <!-- Documents mockup -->
          <div class="lg:col-span-7" aria-hidden="true">
            <div class="rounded-2xl overflow-hidden bg-white shadow-[0_32px_70px_-20px_rgba(51,51,51,0.40)] border border-[rgba(51,51,51,0.08)] flex">
              <!-- Mini sidebar -->
              <div class="hidden sm:flex w-12 shrink-0 flex-col items-center gap-2.5 bg-[#1E1B2E] py-4">
                <span v-for="n in 7" :key="n"
                  class="w-7 h-7 rounded-lg flex items-center justify-center"
                  :class="n === 6 ? 'bg-[#A855F7] text-white' : 'text-white/40'"
                >
                  <GwIcon name="folder-open" class="w-3.5 h-3.5" />
                </span>
              </div>

              <div class="flex-1 min-w-0 p-3 sm:p-4 bg-[#F8F7FC]">
                <!-- Header -->
                <div class="flex flex-wrap items-center gap-2 mb-3">
                  <h3 class="text-[0.95rem] font-bold text-[color:var(--gw-ink)] tracking-[-0.02em]">
                    {{ get('pages.backoffice.documents.mock.title') }}
                  </h3>
                  <div class="flex-1 min-w-[8rem] h-8 rounded-lg bg-white border border-[rgba(51,51,51,0.08)] flex items-center px-2.5 gap-1.5">
                    <GwIcon name="search" class="w-3.5 h-3.5 text-[rgba(51,51,51,0.35)] shrink-0" />
                    <span class="text-[0.65rem] text-[rgba(51,51,51,0.4)] truncate">{{ get('pages.backoffice.documents.mock.search') }}</span>
                  </div>
                  <span class="inline-flex items-center gap-1 rounded-lg bg-[#A855F7] text-white text-[0.7rem] font-semibold px-2.5 py-1.5">
                    + {{ get('pages.backoffice.documents.mock.new') }}
                  </span>
                </div>

                <!-- Folders -->
                <p class="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-[rgba(51,51,51,0.45)] mb-2">
                  {{ get('pages.backoffice.documents.mock.my_folders') }}
                </p>
                <div class="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2 mb-4">
                  <div
                    v-for="folder in docFolders"
                    :key="folder.key"
                    class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] p-2 text-center"
                  >
                    <span
                      class="mx-auto w-8 h-7 rounded-md flex items-center justify-center"
                      :style="{ background: folder.soft, color: folder.tone }"
                    >
                      <GwIcon name="folder" class="w-4 h-4" />
                    </span>
                    <p class="mt-1 text-[0.6rem] font-semibold text-[color:var(--gw-ink)] truncate">
                      {{ get(`pages.backoffice.documents.mock.folders.${folder.key}`) }}
                    </p>
                    <p class="text-[0.5rem] text-[rgba(51,51,51,0.45)]">{{ folder.count }}</p>
                  </div>
                </div>

                <!-- Table -->
                <div class="rounded-xl bg-white border border-[rgba(51,51,51,0.06)] overflow-hidden">
                  <div class="hidden sm:grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.6fr)_1.5rem] gap-2 px-3 py-2 border-b border-[rgba(51,51,51,0.06)] text-[0.55rem] uppercase tracking-[0.1em] font-semibold text-[rgba(51,51,51,0.4)]">
                    <span>{{ get('pages.backoffice.documents.mock.cols.name') }}</span>
                    <span>{{ get('pages.backoffice.documents.mock.cols.linked') }}</span>
                    <span>{{ get('pages.backoffice.documents.mock.cols.type') }}</span>
                    <span>{{ get('pages.backoffice.documents.mock.cols.modified') }}</span>
                    <span>{{ get('pages.backoffice.documents.mock.cols.size') }}</span>
                    <span></span>
                  </div>
                  <div
                    v-for="(row, i) in docRows"
                    :key="i"
                    class="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.6fr)_1.5rem] gap-1 sm:gap-2 items-center px-3 py-2 border-b border-[rgba(51,51,51,0.05)] last:border-0"
                  >
                    <div class="flex items-center gap-2 min-w-0">
                      <span
                        class="w-6 h-6 rounded text-[0.45rem] font-bold text-white flex items-center justify-center shrink-0"
                        :style="{ background: row.typeTone }"
                      >{{ row.type }}</span>
                      <span class="text-[0.7rem] font-medium text-[color:var(--gw-ink)] truncate">{{ row.name }}</span>
                    </div>
                    <span class="text-[0.65rem] text-[rgba(51,51,51,0.55)] truncate">{{ row.linked }}</span>
                    <span>
                      <span
                        class="inline-flex text-[0.55rem] font-semibold px-1.5 py-0.5 rounded-md"
                        :style="{ background: row.tagTone + '22', color: row.tagTone }"
                      >{{ get(`pages.backoffice.documents.mock.tags.${row.tag}`) }}</span>
                    </span>
                    <span class="text-[0.65rem] text-[rgba(51,51,51,0.5)]">{{ row.date }}</span>
                    <span class="text-[0.65rem] text-[rgba(51,51,51,0.5)]">{{ row.size }}</span>
                    <span class="text-[rgba(51,51,51,0.3)] text-center">⋮</span>
                  </div>
                </div>

                <p class="mt-2.5 text-[0.7rem] text-[rgba(51,51,51,0.5)]">
                  {{ get('pages.backoffice.documents.mock.total') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Devices note -->
      <div class="mt-10 lg:mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 reveal">
        <div class="flex items-center gap-3 text-[rgba(51,51,51,0.55)]">
          <span class="flex items-center gap-2.5" aria-hidden="true">
            <GwIcon name="device-desktop" class="w-5 h-5 shrink-0" />
            <GwIcon name="device-tablet" class="w-5 h-5 shrink-0" />
            <GwIcon name="device-phone" class="w-5 h-5 shrink-0" />
          </span>
          <p class="text-[0.875rem] font-medium text-[color:var(--gw-ink)]">
            {{ get('pages.backoffice.devices_note') }}
          </p>
        </div>
        <a :href="featuresHref" class="gw-btn-ghost">
          {{ get('pages.backoffice.cta_features') }}
          <GwIcon name="arrow-right" class="w-4 h-4" />
        </a>
      </div>

      <!-- Included in all packages -->
      <div class="mt-10 lg:mt-12 reveal flex justify-center">
        <div
          class="flex w-full max-w-3xl flex-col gap-4 rounded-2xl border border-[rgba(168,85,247,0.12)] bg-white px-4 py-4 shadow-[0_12px_40px_-16px_rgba(168,85,247,0.35)] sm:flex-row sm:items-center sm:gap-5 sm:px-5 sm:py-3.5"
        >
          <div class="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style="background: rgba(168,85,247,0.14); color: #A855F7;"
              aria-hidden="true"
            >
              <GwIcon name="check" class="h-4 w-4" />
            </span>
            <div class="min-w-0 text-left">
              <p class="text-[0.875rem] font-semibold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-snug sm:text-[0.95rem]">
                {{ get('pages.backoffice.included_banner.title') }}
              </p>
              <p class="mt-0.5 text-[0.75rem] leading-snug text-[rgba(51,51,51,0.55)] sm:text-[0.8rem]">
                {{ get('pages.backoffice.included_banner.subtitle') }}
              </p>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2 self-start sm:self-center">
            <a :href="webRoute('web.ecommerce')" class="gw-btn-ghost">
              {{ get('pages.backoffice.included_banner.cta_ecommerce') }}
              <GwIcon name="arrow-right" class="h-4 w-4" />
            </a>
            <a :href="webRoute('web.landing-page')" class="gw-btn-ghost">
              {{ get('pages.backoffice.included_banner.cta_landing') }}
              <GwIcon name="arrow-right" class="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
